// src/config/reactQuery.ts
import { QueryClient, QueryCache, MutationCache } from '@tanstack/react-query';
import { recordQueryError } from '../utils/networkMonitor';

// P3 : événement personnalisé pour afficher un message utilisateur en cas d'erreur réseau
export const NETWORK_ERROR_EVENT = 'react-query-network-error';

// P4 : événement pour déclencher un refetch manuel (bouton « Réessayer »)
export const REFETCH_QUERIES_EVENT = 'react-query-refetch-queries';

function isNetworkError(error: any): boolean {
  const msg = error?.message?.toLowerCase() ?? '';
  return (
    msg.includes('failed to fetch') ||
    msg.includes('network request failed') ||
    msg.includes('network error') ||
    error?.code === 'unavailable' ||
    error?.code === 'resource-exhausted'
  );
}

// Gestionnaire d'erreurs global pour React Query
const handleQueryError = (error: any) => {
  console.error('❌ [React Query] Erreur détectée:', error);
  console.error('❌ Code erreur:', error?.code);
  console.error('❌ Message:', error?.message);

  // P3 : message clair pour l'utilisateur en cas d'erreur réseau / timeout ; P9 : monitoring
  if (typeof window !== 'undefined' && isNetworkError(error)) {
    recordQueryError(error?.message);
    window.dispatchEvent(
      new CustomEvent(NETWORK_ERROR_EVENT, {
        detail: {
          /** Clé i18n `common:network.errorEvent` — traduit côté banner. */
          code: 'network.errorEvent',
        },
      })
    );
  }

  // Erreur d'index Firestore
  if (error?.code === 'failed-precondition' || error?.message?.includes('index')) {
    console.error('🔥🔥🔥 ERREUR D\'INDEX FIRESTORE 🔥🔥🔥');
    console.error('Une requête nécessite un index composite.');
    
    // Extraire l'URL de création d'index
    const urlMatch = error?.message?.match(/(https:\/\/console\.firebase\.google\.com\/[^\s]+)/);
    if (urlMatch) {
      console.error('🔗 URL pour créer l\'index:', urlMatch[1]);
      console.error('👆 Cliquez sur le lien ci-dessus pour créer l\'index automatiquement');
    }
  }
  
  // Erreur de permissions
  if (error?.code === 'permission-denied') {
    console.error('🔒 ERREUR DE PERMISSIONS FIRESTORE');
    console.error('L\'utilisateur n\'a pas les droits pour accéder à ces données');
    console.error('Vérifiez vos règles de sécurité Firestore (firestore.rules)');
  }
  
  // Quota dépassé
  if (error?.code === 'resource-exhausted') {
    console.error('🚨 QUOTA FIRESTORE DÉPASSÉ');
    console.error('Vous avez dépassé les limites gratuites de Firestore');
  }
};

export const queryClient = new QueryClient({
  // Gestionnaires d'erreurs globaux via QueryCache et MutationCache
  queryCache: new QueryCache({
    onError: handleQueryError,
  }),
  mutationCache: new MutationCache({
    onError: handleQueryError,
  }),
  
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // P3 : refetch au retour du réseau pour rafraîchir les données après une coupure
      refetchOnReconnect: true,
      refetchInterval: false,
      
      // Retry sur erreur avec délai exponentiel
      retry: (failureCount, error) => {
        // Ne pas retry les erreurs d'index Firebase
        if (error instanceof Error && (error.message.includes('query requires an index') || error.message.includes('index'))) {
          return false;
        }
        // Ne pas retry les erreurs d'authentification
        if (error instanceof Error && (
          error.message.includes('UTILISATEUR_SANS_INSTITUT') ||
          error.message.includes('permission-denied') ||
          error.message.includes('unauthenticated')
        )) {
          return false;
        }
        // Retry max 2 fois pour les autres erreurs
        return failureCount < 2;
      },
      
      // Délai avant retry
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      
      // P7 : cache généreux = affichage cache puis refetch en arrière-plan (stale-while-revalidate)
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 15 * 60 * 1000,   // 15 minutes
    },
    mutations: {
      retry: false, // Pas de retry pour les mutations
    },
  },
});

// Version sans retry, utilisée dans App.tsx (P3 refetch reconnect, P7 stale-while-revalidate)
export const queryClientNoRetry = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchInterval: false,
      staleTime: 10 * 60 * 1000, // P7 : 10 min – afficher le cache puis mettre à jour en arrière-plan
      gcTime: 15 * 60 * 1000,
    },
    mutations: {
      retry: false,
    },
  },
});

// UTILISATION DANS APP.TSX :
/*
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './config/reactQuery';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      // Vos autres providers (AuthProvider, etc.)
      // Vos routes et composants
    </QueryClientProvider>
  );
}

// OU pour stopper complètement les boucles :
import { queryClientNoRetry } from './config/reactQuery';
<QueryClientProvider client={queryClientNoRetry}>
*/